import { type Band, bandFor } from "../types.js";
import {
  type Household,
  type IncomeTaxInput,
  IncomeTaxInputSchema,
  type IncomeTaxOutput,
  type IncomeTaxParsed,
  type IncomeTaxRuleSet,
  type HelpRepaymentBand,
  type LitoRules,
  type MedicareLevyRules,
  type Residency,
  type SaptoRules,
} from "./types.js";

// ---------------------------------------------------------------------------
// Pure calculation functions. Each is independently exported so foreground
// tools can compose only the piece they need (e.g. a LITO-only calculator, a
// HELP-only warning) without recomputing the whole assessment. No I/O, no
// Date.now(); the FY/ruleset is always a parameter.
// ---------------------------------------------------------------------------

/**
 * Marginal income tax over a bracket schedule. Each bracket charges its `rate`
 * on income in `[from, to)`; tax is the sum across all brackets up to `income`.
 * Works for resident, foreign and WHM schedules alike (they differ only in the
 * bracket data). Rounded to whole dollars.
 */
export function taxOnSchedule(
  brackets: readonly Band[],
  income: number,
): number {
  let tax = 0;
  for (const b of brackets) {
    if (b.rate === 0) continue;
    // Brackets are authored `[from, to)` with `from` = published-edge + 1
    // (e.g. 18,201) and `to` = next-from (e.g. 45,001). The published band runs
    // (from-1, to-1], so tax accrues on income in (loEdge, hiEdge].
    //
    // A first band that genuinely starts at the first dollar is authored
    // `{from: 0}` (foreign/WHM "from $1") — `from - 1` would be -1 and tax a
    // phantom dollar, so clamp the low edge at 0.
    const loEdge = Math.max(0, b.from - 1); // e.g. 18,200, or 0 for from-$1 bands
    const hiEdge = b.to === null ? income : b.to - 1; // e.g. 45,000
    const taxed = Math.min(income, hiEdge) - loEdge;
    if (taxed > 0) tax += b.rate * taxed;
  }
  return Math.round(tax);
}

/**
 * Pick the bracket schedule for a residency status. The engine never decides
 * residency — it is supplied.
 */
export function bracketsFor(
  rules: IncomeTaxRuleSet,
  residency: Residency,
): readonly Band[] {
  switch (residency) {
    case "resident":
      return rules.residentBrackets;
    case "foreign":
      return rules.foreignBrackets;
    case "whm":
      return rules.whmBrackets;
  }
}

/**
 * Resident tax-free threshold for the assessment, accounting for a part-year
 * resident. `residentMonths` is inclusive of the arrival month (ATO
 * convention). 12 (or null) → full threshold; 1–11 → pro-rated:
 * `base + proRated × months / 12`. Foreign/WHM get 0 (no tax-free threshold);
 * that is handled by the bracket schedules, so this only matters for residents.
 */
export function residentTaxFreeThreshold(
  rules: IncomeTaxRuleSet,
  residentMonths: number | null,
): number {
  if (residentMonths === null || residentMonths >= 12) {
    return rules.taxFreeThreshold;
  }
  const { base, proRated } = rules.partYearThreshold;
  // ATO rounds to the nearest dollar; the published worked examples use exact
  // dollar results (e.g. 13,464 + 4,736×3/12 = 14,648).
  return Math.round(base + (proRated * residentMonths) / 12);
}

/**
 * Income tax for a resident given a (possibly pro-rated) tax-free threshold.
 * The resident bracket schedule already encodes the standard $18,200 threshold;
 * when the threshold is pro-rated DOWN (part-year resident) the extra slice
 * between the pro-rated threshold and $18,200 becomes taxable at the first
 * marginal rate. We model this by taxing on the standard schedule and adding
 * the first-rate tax on the (taxFreeThreshold − proRatedThreshold) slice.
 */
function residentIncomeTax(
  rules: IncomeTaxRuleSet,
  income: number,
  threshold: number,
): number {
  const standard = taxOnSchedule(rules.residentBrackets, income);
  if (threshold >= rules.taxFreeThreshold) return standard;
  // Part-year: the slice (threshold, taxFreeThreshold] is now taxable. The
  // first marginal rate applies to it (it sits in the first non-nil bracket).
  const firstRate = rules.residentBrackets[1]?.rate ?? 0;
  const exposedSlice = Math.min(income, rules.taxFreeThreshold) - threshold;
  if (exposedSlice <= 0) return standard;
  return Math.round(standard + firstRate * exposedSlice);
}

/** Gross income tax before offsets/levy, dispatched by residency. */
export function incomeTaxFor(
  rules: IncomeTaxRuleSet,
  input: Pick<IncomeTaxParsed, "taxableIncome" | "residency" | "residentMonths">,
): number {
  if (input.residency === "resident") {
    const threshold = residentTaxFreeThreshold(rules, input.residentMonths);
    return residentIncomeTax(rules, input.taxableIncome, threshold);
  }
  return taxOnSchedule(bracketsFor(rules, input.residency), input.taxableIncome);
}

/**
 * Medicare levy after the low-income reduction. Residents only — foreign
 * residents and WHMs are exempt (return 0). Mechanic: nil at/below the lower
 * threshold; shaded in at `reductionRate` (10c) per $1 above the lower
 * threshold; full `rate` (2%) of taxable income at/above the upper threshold.
 * The applicable threshold pair depends on household status (single / family /
 * senior-pensioner) with per-dependant uplifts for families.
 */
export function medicareLevyFor(
  m: MedicareLevyRules,
  taxableIncome: number,
  household: Household,
  residency: Residency,
): number {
  if (residency !== "resident") return 0;

  let lower: number;
  let upper: number;
  if (household.seniorPensioner && !household.family) {
    lower = m.saptoSingleLowerThreshold;
    upper = m.saptoSingleUpperThreshold;
  } else if (household.family) {
    const kids = Math.max(0, household.dependants);
    lower = m.familyLowerThreshold + kids * m.familyChildLowerIncrement;
    upper = m.familyUpperThreshold + kids * m.familyChildUpperIncrement;
  } else {
    lower = m.singleLowerThreshold;
    upper = m.singleUpperThreshold;
  }

  const fullLevy = m.rate * taxableIncome;
  if (taxableIncome <= lower) return 0;
  if (taxableIncome >= upper) return Math.round(fullLevy);
  // Shade-in: levy is reductionRate × (income − lower), capped below fullLevy.
  const shaded = m.reductionRate * (taxableIncome - lower);
  return Math.round(Math.min(shaded, fullLevy));
}

/**
 * Low Income Tax Offset (LITO). Residents only; non-refundable (the caller caps
 * it against tax payable). Returns the gross offset before that cap.
 *   ≤taperStart        : max
 *   taperStart..taper2Start : max − taper1Rate × (income − taperStart)
 *   taper2Start..cutOut : max − (taper1 full) − taper2Rate × (income − taper2Start)
 *   ≥cutOut            : 0
 */
export function litoFor(
  l: LitoRules,
  taxableIncome: number,
  residency: Residency,
): number {
  if (residency !== "resident") return 0;
  if (taxableIncome <= l.taperStart) return l.max;
  if (taxableIncome >= l.cutOut) return 0;
  if (taxableIncome <= l.taper2Start) {
    return l.max - l.taper1Rate * (taxableIncome - l.taperStart);
  }
  const afterStage1 = l.max - l.taper1Rate * (l.taper2Start - l.taperStart);
  const offset = afterStage1 - l.taper2Rate * (taxableIncome - l.taper2Start);
  return Math.max(0, offset);
}

/**
 * Seniors and Pensioners Tax Offset (SAPTO), single path. Residents flagged as
 * senior/pensioner only; non-refundable. Returns gross offset before cap.
 *   ≤shadeOut : maxSingle
 *   shadeOut..cutOut : maxSingle − taperRate × (rebateIncome − shadeOut)
 *   ≥cutOut  : 0
 * We use taxable income as a proxy for rebate income (the two coincide for the
 * salary-and-wage cohort this engine serves; rebate-income add-backs are out of
 * scope and documented in the ledger).
 */
export function saptoFor(
  s: SaptoRules,
  rebateIncome: number,
  household: Household,
  residency: Residency,
): number {
  if (residency !== "resident" || !household.seniorPensioner) return 0;
  if (household.family) return 0; // couple transfer math out of scope
  if (rebateIncome <= s.shadeOutSingle) return s.maxSingle;
  if (rebateIncome >= s.cutOutSingle) return 0;
  return s.maxSingle - s.taperRate * (rebateIncome - s.shadeOutSingle);
}

/** Pick the HELP band whose `[from, to)` contains `income`. */
function helpBandFor(
  bands: readonly HelpRepaymentBand[],
  income: number,
): HelpRepaymentBand {
  for (const band of bands) {
    if (income >= band.from && (band.to === null || income < band.to)) {
      return band;
    }
  }
  throw new Error(`no HELP band matched repayment income ${income}`);
}

/**
 * Compulsory HELP/STSL repayment under the FY2025-26 marginal model. Charged
 * only on income above each band floor (the "marginal, not flat-%" wedge),
 * except the top band, which charges `rate` × WHOLE repayment income. Returns 0
 * below the minimum threshold.
 */
export function helpRepaymentFor(
  rules: IncomeTaxRuleSet,
  repaymentIncome: number,
): number {
  const band = helpBandFor(rules.helpBands, repaymentIncome);
  if (band.rate === 0) return 0;
  if (band.wholeIncome) return Math.round(repaymentIncome * band.rate);
  const thresholdFloor = band.from - 1;
  return Math.round(band.baseAmount + band.rate * (repaymentIncome - thresholdFloor));
}

/** Marginal income-tax rate on the next dollar (excludes levy + HELP). */
function marginalRateFor(brackets: readonly Band[], income: number): number {
  return bandFor(brackets, income).rate;
}

/**
 * Full assessment: income tax, Medicare levy, LITO/SAPTO offsets, compulsory
 * HELP repayment, and take-home. Pure — same inputs + ruleset always yield the
 * same output. Validates at the boundary so malformed payloads fail loudly.
 */
export function calculateIncomeTax(
  rawInput: IncomeTaxInput,
  rules: IncomeTaxRuleSet,
): IncomeTaxOutput {
  const input = IncomeTaxInputSchema.parse(rawInput);

  const taxFreeThreshold =
    input.residency === "resident"
      ? residentTaxFreeThreshold(rules, input.residentMonths)
      : 0;

  const incomeTax = incomeTaxFor(rules, input);

  const medicareLevy = medicareLevyFor(
    rules.medicareLevy,
    input.taxableIncome,
    input.household,
    input.residency,
  );

  // Offsets are non-refundable: together they can only reduce income tax to $0,
  // and never the Medicare levy. Apply LITO + SAPTO against income tax, capped.
  const grossLito = litoFor(rules.lito, input.taxableIncome, input.residency);
  const grossSapto = saptoFor(
    rules.sapto,
    input.taxableIncome,
    input.household,
    input.residency,
  );
  const totalOffset = Math.min(incomeTax, grossLito + grossSapto);
  // Attribute the capped total back to each offset for transparency (SAPTO
  // first — it is the larger, senior-specific offset — then LITO).
  const sapto = Math.round(Math.min(grossSapto, totalOffset));
  const lito = Math.round(Math.max(0, totalOffset - sapto));

  const netIncomeTax = Math.max(0, incomeTax - lito - sapto);
  const netTax = netIncomeTax + medicareLevy;

  const helpRepaymentIncome = input.repaymentIncome ?? input.taxableIncome;
  const helpRepayment = input.hasStudyLoan
    ? helpRepaymentFor(rules, helpRepaymentIncome)
    : 0;

  const takeHome = input.taxableIncome - netTax - helpRepayment;

  return {
    taxableIncome: input.taxableIncome,
    residency: input.residency,
    taxFreeThreshold,
    incomeTax,
    medicareLevy,
    lito,
    sapto,
    netTax,
    helpRepayment,
    helpRepaymentIncome,
    takeHome,
    marginalRate: marginalRateFor(
      bracketsFor(rules, input.residency),
      input.taxableIncome,
    ),
  };
}
